<template>
    <form class="pe" @submit.prevent="submit(closes ? 'invoice' : 'abono')">
        <!-- Quién paga (solo al dividir) -->
        <InputText v-if="mode === 'split'" v-model="label" placeholder="Quién paga (ej. José)" maxlength="60"
                   aria-label="Quién paga" />

        <!-- Forma de pago -->
        <div class="pe-field">
            <label class="pe-label">Forma de pago</label>
            <SelectButton v-model="method" :options="methods" optionLabel="short" optionValue="value"
                          :unselectable="false" class="pe-methods" :class="{ 'pe-invalid': errorField === 'method' }"
                          aria-label="Forma de pago" />
        </div>

        <!-- Cuenta de tesorería: obligatoria con contabilidad automática; opcional si hay cuentas -->
        <div v-if="showAccount" class="pe-field">
            <label class="pe-label">{{ setup.requiresAccount ? 'Entra a la cuenta' : 'Entra a la cuenta (opcional)' }}</label>
            <Dropdown v-model="accountId" :options="accountOptions" optionLabel="name" optionValue="id"
                      :placeholder="accountOptions.length ? 'Elige la cuenta' : 'Sin cuentas de este tipo'"
                      :showClear="!setup.requiresAccount" :class="{ 'p-invalid': errorField === 'account' }"
                      aria-label="Cuenta donde entra el pago" />
            <small v-if="noAccountsNotice" class="pe-warn">{{ noAccountsNotice }}</small>
        </div>

        <!-- Monto -->
        <div v-if="mode === 'split'" class="pe-field">
            <label class="pe-label">Monto <span class="pe-muted">(falta ${{ money(due) }})</span></label>
            <InputNumber v-model="amount" mode="currency" currency="USD" locale="en-US" :min="0" :max="due"
                         :class="{ 'p-invalid': errorField === 'amount' }" aria-label="Monto que paga" />
        </div>

        <!-- Propina -->
        <div class="pe-field">
            <label class="pe-label">Propina</label>
            <div class="pe-tips">
                <button v-for="opt in tipOptions" :key="opt.value" type="button" class="pe-chip"
                        :class="{ 'pe-chip-on': tipMode === opt.value }" @click="pickTip(opt.value)">
                    {{ opt.label }}
                </button>
                <InputNumber v-if="tipMode === 'custom'" v-model="tipCustom" mode="currency" currency="USD"
                             locale="en-US" :min="0" class="pe-tip-input" :class="{ 'p-invalid': errorField === 'tip' }"
                             aria-label="Monto de la propina" />
            </div>
        </div>

        <!-- Lo que de verdad entra (obligatorio, cubre el pago + la propina, como el cobro de una venta):
             el efectivo recibido, con su cambio, o el valor cargado en el voucher / la transferencia -->
        <div v-if="method === 'CASH'" class="pe-field pe-cash">
            <div>
                <label class="pe-label">Recibido</label>
                <InputNumber v-model="received" mode="currency" currency="USD" locale="en-US" :min="0"
                             :class="{ 'p-invalid': errorField === 'received' }" aria-label="Efectivo recibido" />
            </div>
            <div class="pe-change" :class="{ 'pe-change-bad': change !== null && change < 0 }">
                <span class="pe-label">Cambio</span>
                <b>{{ change === null ? '—' : '$' + money(Math.max(change, 0)) }}</b>
            </div>
        </div>
        <div v-else class="pe-field">
            <label class="pe-label">Valor cobrado <span class="pe-muted">(pago + propina)</span></label>
            <InputNumber v-model="received" mode="currency" currency="USD" locale="en-US" :min="0"
                         :class="{ 'p-invalid': errorField === 'received' }" aria-label="Valor cobrado" />
        </div>
        <button v-if="excessAsTip > 0" type="button" class="pe-suggest" @click="applyExcessAsTip">
            <i class="pi pi-lightbulb"></i>
            {{ method === 'CASH' ? '¿Deja el cambio' : '¿El excedente' }} (${{ money(excessAsTip) }}) {{ method === 'CASH' ? 'de propina?' : 'es propina?' }}
        </button>

        <!-- Voucher de tarjeta / comprobante de transferencia -->
        <template v-if="voucherApplies">
            <div v-if="accountId" class="pe-field">
                <template v-if="isCardMethod">
                    <label class="pe-label">Nº de aprobación del voucher</label>
                    <InputText v-model="approvalNumber" inputmode="numeric" maxlength="60" placeholder="Ej. 004512"
                               :class="{ 'p-invalid': errorField === 'approval' }" aria-label="Número de aprobación" />
                </template>
                <label class="pe-label">
                    {{ isCardMethod ? 'Foto del voucher (opcional)' : (setup.requiresProof ? 'Comprobante *' : 'Comprobante (opcional)') }}
                </label>
                <ProofUpload v-model="proof" :invalid="errorField === 'proof'" />
            </div>
            <small v-else class="pe-muted">{{ voucherHint }}</small>
        </template>

        <!-- Lo que se cobra -->
        <div class="pe-sum">
            <span>{{ closes ? 'A cobrar' : 'Este pago' }} ${{ money(amountValue) }}<template v-if="tip > 0">
                + propina ${{ money(tip) }}</template></span>
            <b>${{ money(totalCharge) }}</b>
        </div>

        <!-- Datos de factura (los pone el padre): solo cuando este pago cobra lo que falta y cierra -->
        <slot v-if="closes" />

        <div class="pe-actions">
            <template v-if="!closes">
                <Button type="submit" :label="'Agregar pago $' + money(totalCharge)" icon="pi pi-plus"
                        :loading="saving" :disabled="saving" />
            </template>
            <template v-else>
                <Button type="submit" :label="'Facturar $' + money(totalCharge)" icon="pi pi-receipt"
                        :loading="saving && intent === 'invoice'" :disabled="saving" />
                <Button type="button" :label="'Archivar $' + money(totalCharge)" class="p-button-text p-button-sm"
                        :loading="saving && intent === 'archive'" :disabled="saving"
                        @click="submit('archive')" />
            </template>
        </div>
    </form>
</template>

<script>
import { round2 } from '../Accounting/accountEntryRules';
import ProofUpload from '../Accounting/ProofUpload.vue';
import {
    SALON_METHODS, accountsFor, defaultAccountId, tipFromPercent, cashChange, paymentError, isCard, takesProof,
    moneyTypeFor,
} from '../../service/paymentRules';

const MONEY_TYPE_NAMES = { CASH: 'efectivo', CARD: 'tarjeta', TRANSFER: 'transferencia' };

export default {
    name: 'PaymentEntryForm',
    components: { ProofUpload },
    props: {
        mode: { type: String, default: 'split' },       // 'split' = una persona · 'full' = todo de una vez
        due: { type: Number, required: true },          // lo que falta cobrar de la cuenta
        setup: { type: Object, default: () => ({}) },   // paymentSetup del servidor
        saving: { type: Boolean, default: false },
        serverError: { type: Object, default: null },   // {field} que devolvió el servidor
    },
    emits: ['submit'],
    data() {
        return {
            label: '',
            method: 'CASH',
            accountId: null,
            amount: this.due,
            tipMode: 'none',
            tipCustom: null,
            received: null,
            approvalNumber: '',
            proof: null,
            errorField: null,
            intent: null,
            methods: SALON_METHODS,
            tipOptions: [
                { value: 'none', label: 'Sin propina' },
                { value: 'p10', label: '10%' },
                { value: 'custom', label: 'Otra' },
            ],
        };
    },
    computed: {
        accounts() { return (this.setup && this.setup.accounts) || []; },
        accountOptions() { return accountsFor(this.accounts, this.method); },
        showAccount() { return !!(this.setup.requiresAccount || this.accounts.length); },
        noAccountsNotice() {
            if (!this.showAccount || this.accountOptions.length) return null;
            const type = MONEY_TYPE_NAMES[moneyTypeFor(this.method)];
            return this.setup.requiresAccount
                ? `No hay cuentas de ${type}: créala en Finanzas › Cuentas para poder cobrar así.`
                : `No hay cuentas de ${type}: el pago se registra sin cuenta.`;
        },
        amountValue() { return this.mode === 'full' ? round2(this.due) : round2(this.amount || 0); },
        // Este pago cobra todo lo que falta: no es un abono más, es el cobro con el que la cuenta se
        // cierra (y se factura), en un solo paso. Al dividir, el último en pagar llega aquí.
        closes() { return this.mode === 'full' || (this.due > 0 && this.amountValue >= round2(this.due)); },
        tip() {
            if (this.tipMode === 'p10') return tipFromPercent(this.amountValue, 10);
            if (this.tipMode === 'custom') return round2(this.tipCustom || 0);
            return 0;
        },
        totalCharge() { return round2(this.amountValue + this.tip); },
        change() { return this.method === 'CASH' ? cashChange(this.received, this.amountValue, this.tip) : null; },
        // Si entró más que el pago y aún no hay propina, sugerir que el excedente es propina (como
        // AccountEntry): en efectivo, dejar el cambio; con tarjeta o transferencia no hay cambio.
        excessAsTip() {
            if (this.tipMode !== 'none') return 0;
            const over = cashChange(this.received, this.amountValue, this.tip);
            return over !== null && over > 0 ? over : 0;
        },
        isCardMethod() { return isCard(this.method); },
        voucherApplies() { return isCard(this.method) || takesProof(this.method); },
        voucherHint() {
            if (!this.accounts.length) {
                return 'Para guardar el voucher o el comprobante, crea las cuentas (Caja, Banco, Datáfono) en Finanzas › Cuentas.';
            }
            return 'Elige la cuenta donde entra el pago para guardar el voucher o el comprobante.';
        },
    },
    watch: {
        method: {
            immediate: true,
            handler(method) {
                this.accountId = defaultAccountId(this.accounts, method);
                if (!this.voucherApplies) this.proof = null;
                if (!isCard(method)) this.approvalNumber = '';
                this.received = null;   // efectivo recibido o valor cobrado: se ingresa de nuevo
                this.errorField = null;
            },
        },
        accounts() { this.accountId = defaultAccountId(this.accounts, this.method); },
        due(value) {
            // Cambió lo que falta (otro pago entró, se agregaron ítems): el monto sugerido es «la diferencia».
            if (this.mode === 'split' || this.amount === null || this.amount > value) this.amount = value;
        },
        serverError(err) { this.errorField = err && err.field ? err.field : null; },
        proof(file) { if (file && this.errorField === 'proof') this.errorField = null; },
    },
    methods: {
        money(value) { return Number(value || 0).toFixed(2); },
        pickTip(mode) {
            this.tipMode = mode;
            if (mode === 'custom' && this.tipCustom === null) this.tipCustom = tipFromPercent(this.amountValue, 10);
        },
        applyExcessAsTip() {
            this.tipCustom = this.excessAsTip;
            this.tipMode = 'custom';
        },
        submit(intent) {
            if (this.saving) return;
            const payload = {
                label: (this.label || '').trim() || null, amount: this.amountValue, due: this.due,
                method: this.method, accountId: this.accountId, approvalNumber: (this.approvalNumber || '').trim(),
                proof: this.proof, tip: this.tip, received: this.received,
            };
            const error = paymentError(payload, this.setup);
            if (error) {
                this.errorField = error.field;
                this.$toast.add({ severity: 'warn', summary: 'Revisa el pago', detail: error.message, life: 5000 });
                return;
            }
            this.errorField = null;
            this.intent = intent;
            this.$emit('submit', payload, intent);
        },
        // El padre lo llama tras registrar un pago al dividir: listo para la siguiente persona.
        reset() {
            this.label = '';
            this.amount = this.due;
            this.tipMode = 'none';
            this.tipCustom = null;
            this.received = null;
            this.approvalNumber = '';
            this.proof = null;
            this.errorField = null;
        },
    },
};
</script>

<style scoped>
.pe { display: flex; flex-direction: column; gap: .6rem; }
.pe-field { display: flex; flex-direction: column; gap: .3rem; }
.pe-label { font-size: .78rem; color: var(--text-color-secondary); font-weight: 600; }
.pe-muted { color: var(--text-color-secondary); font-weight: 400; font-size: .8rem; }
.pe-warn { color: #b45309; }
.pe-methods { display: flex; }
.pe-methods :deep(.p-button) { flex: 1; padding-left: .2rem; padding-right: .2rem; font-size: .82rem; }
.pe :deep(.p-inputnumber), .pe :deep(.p-inputtext), .pe :deep(.p-dropdown) { width: 100%; min-width: 0; }
.pe-tips { display: flex; flex-wrap: wrap; gap: .35rem; align-items: center; }
.pe-chip { border: 1px solid var(--surface-border); background: var(--surface-0); border-radius: 999px; font: inherit;
    font-size: .82rem; padding: .3rem .75rem; cursor: pointer; color: var(--text-color); }
.pe-chip-on { background: var(--primary-color); border-color: var(--primary-color); color: var(--primary-color-text); }
.pe-chip:focus-visible { outline: 2px solid var(--primary-color); outline-offset: 2px; }
.pe-tip-input { flex: 1 1 7rem; }
.pe-cash { display: grid; grid-template-columns: minmax(0, 1fr) auto; gap: .6rem; align-items: end; }
.pe-change { display: flex; flex-direction: column; align-items: flex-end; min-width: 5.5rem; }
.pe-change b { font-size: 1.15rem; }
.pe-change-bad b { color: #dc2626; }
.pe-suggest { border: 1px dashed #f59e0b; background: #fffbeb; color: #92400e; border-radius: 8px; font: inherit;
    font-size: .82rem; padding: .4rem .6rem; text-align: left; cursor: pointer; }
.pe-invalid { border-color: #ef4444 !important; }
.pe-sum { display: flex; justify-content: space-between; align-items: baseline; gap: .5rem; font-size: .88rem;
    color: var(--text-color-secondary); border-top: 1px dashed var(--surface-border); padding-top: .5rem; }
.pe-sum b { font-size: 1.1rem; color: var(--text-color); }
.pe-actions { display: flex; flex-direction: column; gap: .35rem; }
.pe-actions :deep(.p-button:not(.p-button-text)) { width: 100%; justify-content: center; }
.pe-actions :deep(.p-button-text) { align-self: center; }
</style>
